/* eslint-disable @typescript-eslint/no-unsafe-return */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { ClientGeoArea } from '@DigitaleDoerfer/digitale-doerfer-api';
import dompurify from 'dompurify';
import { useGetGeoArea, useSearchGeoArea } from 'hooks';
import { FC, useMemo, useState } from 'react';
import AsyncSelect, { ActionMeta, SingleValue } from 'react-select';
import { GeoAreaDropdownItem, prepareDropdownData } from 'utils';

import './GeoAreaSelection.scss';

const DEFAULT_UP_TO_DEPTH = 2;

type DropDownItem = {
  value?: GeoAreaDropdownItem;
  label: string;
};

const selectionStyles = {
  dropdownIndicator: (base: any) => ({
    ...base,
    height: 40,
    color: 'black',
    padding: '0 0.5rem',
    svg: {
      alignSelf: 'center',
      width: 'auto',
      margin: 0
    }
  }),
  menuList: (base: any) => ({
    ...base,
    padding: 0,
    margin: 0
  }),
  container: (base: any) => ({
    ...base,
    width: '100%',
    padding: 0
  }),
  valueContainer: (base: any) => ({
    ...base,
    height: 40,
    padding: '0 0.5rem',
    alignItems: 'center'
  }),
  placeholder: (base: any) => ({
    ...base,
    top: '60%'
  }),
  singleValue: (base: any) => ({
    ...base,
    top: '60%'
  }),
  control: (base: any) => ({
    ...base,
    border: 0,
    borderRadius: 0,
    borderTopRightRadius: '0.25rem',
    borderBottomRightRadius: '0.25rem',
    boxShadow: '0 !important',
    '&:hover': {
      border: 0
    }
  }),
  option: (
    styles: any,
    {
      data,
      isFocused,
      isSelected
    }: {
      data: { value?: GeoAreaDropdownItem };
      isDisabled: boolean;
      isFocused: boolean;
      isSelected: boolean;
    }
  ) => {
    const selectedOrFocusedBgColor =
      isFocused || isSelected ? '#b2d4ff' : 'transparent';
    const isLeaf = data?.value?.leaf;
    return {
      ...styles,
      backgroundColor: !isLeaf ? '#E8EDF5' : selectedOrFocusedBgColor,
      cursor: isLeaf ? 'pointer' : 'auto',
      color: isLeaf ? '#6586B9' : '#868E96',
      textTransform: !isLeaf ? 'uppercase' : 'none',
      fontSize: !isLeaf ? '0.875rem' : '1rem',
      lineHeight: !isLeaf ? '1.0625rem' : '1.375rem',
      padding: !isLeaf ? '0.375rem' : '0.5rem',
      paddingLeft: '1.5rem',
      margin: '0px',
      fontWeight: !isLeaf ? 'bold' : 'normal',
      borderBottom: !isLeaf ? '' : '1px solid #E8EDF5'
    };
  }
};

type GeoAreaSelectionProps = {
  appVariantIdentifier?: string;
  selectedGeoAreaId?: string;
  onChange: (value: string) => void;
};

export const GeoAreaSelection: FC<GeoAreaSelectionProps> = ({
  appVariantIdentifier,
  selectedGeoAreaId,
  onChange
}) => {
  const [search, setSearch] = useState<string>('');

  const { data: foundGeoAreas, isLoading } = useSearchGeoArea({
    search,
    appVariantIdentifier,
    upToDepth: DEFAULT_UP_TO_DEPTH
  });

  const { data: selectedGeoArea } = useGetGeoArea({
    geoAreaId: selectedGeoAreaId ?? '',
    appVariantIdentifier
  });

  const options = useMemo(() => {
    const processedGeoAreas = prepareDropdownData(foundGeoAreas).map(
      (geoArea) => {
        const zipCodes = geoArea.zip?.split(',');
        let geoAreaValue = geoArea;
        if (zipCodes && zipCodes?.length > 1) {
          geoAreaValue = {
            ...geoAreaValue,
            zip: `${zipCodes[0]} - ${zipCodes[zipCodes?.length - 1]}`
          };
        }
        return {
          value: geoAreaValue,
          label: geoArea?.name ?? ''
        };
      }
    );
    return processedGeoAreas;
  }, [foundGeoAreas]);

  const getNoOptionsMessage = (): string => {
    let noOptionsMessage = '';
    if (search.length < 3) {
      noOptionsMessage = 'Bitte gib min. 3 Buchstaben ein';
    } else if (isLoading) {
      noOptionsMessage = 'Wird gesucht...';
    } else {
      noOptionsMessage = 'Gemeinde konnte nicht gefunden werden.';
    }
    return noOptionsMessage;
  };

  const getOptionLabelText = (item?: GeoAreaDropdownItem): string => {
    if (!item) return '';
    const zipCode = item.zip ? `| ${item.zip}` : '';
    return `${item.name} ${zipCode}`;
  };

  /**
   * Bold the given searchText that matches the given text.
   * @param searchText Text that will be bold in the complete text.
   * @param text Complete text that will be replaced.
   */
  const boldSearchedMatchText = (searchText: string, text: string): string =>
    text.replace(
      new RegExp(searchText, 'gi'),
      (match) => `<strong>${match}</strong>`
    );

  const getFormattedOptionLabel = (option: DropDownItem): React.ReactNode => {
    const isLeaf = option.value?.leaf;
    const optionLabelText = getOptionLabelText(option.value);
    return (
      <>
        <div>
          <div
            className="optionLabelTitle"
            dangerouslySetInnerHTML={{
              __html: dompurify.sanitize(
                !isLeaf
                  ? optionLabelText
                  : boldSearchedMatchText(search, optionLabelText)
              )
            }}
          />
          {isLeaf && (
            <div
              className="optionLabelSubtitle"
              dangerouslySetInnerHTML={{
                __html: dompurify.sanitize(
                  boldSearchedMatchText(search, option.value?.path ?? '')
                )
              }}
            />
          )}
        </div>
      </>
    );
  };

  return (
    <AsyncSelect
      styles={selectionStyles}
      inputValue={search}
      value={{ value: selectedGeoArea, label: '' }}
      onChange={(
        newItem: SingleValue<DropDownItem>,
        actionMeta: ActionMeta<DropDownItem>
      ) => {
        onChange(
          actionMeta.action === 'clear'
            ? ''
            : (newItem?.value as ClientGeoArea).id
        );
      }}
      onInputChange={setSearch}
      options={options}
      noOptionsMessage={getNoOptionsMessage}
      placeholder="Gemeinde eingeben..."
      filterOption={(): boolean => true}
      // disable those geoAreas that are not leaves
      isOptionDisabled={(option: DropDownItem): boolean => !option.value?.leaf}
      formatOptionLabel={getFormattedOptionLabel}
    />
  );
};
